import { makeAutoObservable, runInAction, flow } from 'mobx';

import { Account, AccountModel } from '../models/Account';
import { getAccounts, updateAccount, addAccount, deleteAccount } from '../services/AccountsAPI';
import { menuOptions } from '../components/Common/MenuOption';
import { ReferenceData } from '../models/common';
import { errorStore } from './ErrorStore';
import { getActiveDepartmentCode } from '../utils/ActiveDepartment';

class AccountStore {
	data: Account[] = [];
	isLoading: boolean = false;
	showModal: boolean = false;
	currentRow: AccountModel | undefined;

	constructor() {
		makeAutoObservable(this);
	}

	loadData = flow(function* (this: AccountStore) {
		if (this.data.length || this.isLoading) return;
		const departmentCode = getActiveDepartmentCode();

		try {
			this.setLoading(true);
			const response = yield getAccounts();
			if (departmentCode !== getActiveDepartmentCode()) {
				return;
			}

			this.setData(response || []);
		} catch (error: any) {
			this.setError(error.message);
		} finally {
			this.setLoading(false);
		}
	});

	async reloadForActiveDepartment() {
		this.reset();
		await this.loadData();
	}

	async delete(account: Account) {
		try {
			this.setLoading(true);
			await deleteAccount(account.id)
			this.setData(this.data.filter(a => a.id !== account.id));
		} catch (error: any) {
			this.setError(error.message);
		} finally {
			this.setLoading(false);
		}
	}

	async update(editedAccount: Account) {
		try {
			this.setLoading(true);
			await updateAccount(editedAccount.id, editedAccount);

			runInAction(() => {
				this.data = this.data.map(a =>
					a.id === editedAccount.id ? { ...editedAccount } : a
				);
			});
			this.removeCurrent();
		} catch (error: any) {
			this.setError(error.message);
			throw error;
		} finally {
			this.setLoading(false);
		}
	}

	addNew = flow(function* (this: AccountStore, newAccount: Account, onSuccess?: () => void) {
		try {
			this.setLoading(true);
			const addedNewAccount = yield addAccount(newAccount);
			if (addedNewAccount) {
				this.addRow(addedNewAccount);
				this.clearForm();
				onSuccess?.();
			}
		} catch (error: any) {
			this.setError(error.message)
		} finally {
			this.setLoading(false);
		}
	})

	setupNew = () => {
		runInAction(() => {
			this.setCurrent(new AccountModel({ id: '', name: '', isActive: true }));
			this.setShowModal(true);
		});
	};

	get options() {
		return this.data as ReferenceData[];
	};

	get requiredOptions() {
		return menuOptions(this.data);
	}

	get prologueId() {
		return this.data.find(account => account.name === 'Prologue')?.id;
	}

	getName(id?: string) {
		return this.data.find(a => a.id === id)?.name || 'No Account Found';
	}

	setLoading(loading: boolean) {
		this.isLoading = loading;
	}

	setError(error: string) {
		errorStore.report(error);
	}

	addRow(account: Account) {
		this.data = [account, ...this.data];
	}

	setShowModal(state: boolean) {
		this.showModal = state;
	}

	setCurrent(currentAccount: AccountModel) {
		this.currentRow = currentAccount;
	}

	removeCurrent() {
		this.currentRow = undefined;
	}

	clearForm() {
		this.removeCurrent();
		this.setShowModal(false);
	}

	reset() {
		this.data = [];
		this.isLoading = false;
		this.clearForm();
	}

	setData(accounts: Account[]) {
		this.data = accounts;
	}
}

export const accountStore = new AccountStore();

